/**
 * NAT-2335 — when a stored "acting as" selection must be given up.
 *
 * The selection outlives the session in localStorage. Since NAT-2335 the server
 * REFUSES a client the caller is not entitled to rather than quietly serving
 * it, and those two facts meet the moment a client is reassigned to another
 * agent — the intended way to hand a customer over — while the old agent's
 * browser still holds that id.
 *
 * Left alone that is not a stale dropdown: every catalogue and cart request
 * for the stale client is refused with a 403 until the person re-picks. (Until
 * NAT-2494 it was worse — the refresh interceptor was registered on [401, 403],
 * read the 403 as an expired token, and on a failed refresh cleared every auth
 * key and redirected to login. It now refreshes on 401 only.)
 *
 * Pulled out of the component because the guards are the whole point and they
 * are easier to state — and to pin — as one function than as an effect body.
 */

type Option = { value: string | number };

/**
 * NAT-2494 — an acting role with no client selected must not write.
 *
 * `resolveOrderingClientId` treats an absent id as "nobody named" and resolves
 * an admin to their OWN venue. So an admin who cleared the picker (or had it
 * retired by `shouldRetireActingClient`) while the cart still showed the last
 * client's items would, on the next "+", add a bottle to the admin's own
 * venue's cart — and a confirm from the summary tab would confirm THAT venue's
 * order. The old payload (`{ selectedAgentClientId: '' }` parsed raw) happened
 * to be refused as non-numeric; correct by accident. This is the rule stated.
 *
 * A customer is never blocked here: inside the acting-client gate they always
 * carry a venue (NAT-2497), and the gate-less routes have no write affordances.
 */
export function actingWithoutClient(params: {
  canActAsClient: boolean;
  selected?: string | number | null;
}): boolean {
  const { canActAsClient, selected } = params;
  if (!canActAsClient) return false;
  return selected === undefined || selected === null || selected === '';
}

export function shouldRetireActingClient(params: {
  /** Whether this role acts for a client at all. A customer never retires anything. */
  canActAsClient: boolean;
  /** The currently selected client id, from state or storage. */
  selected?: string | number | null;
  /**
   * The raw list as returned by the hook. `undefined` means the request has not
   * settled — distinct from an empty array, which is a settled empty answer.
   */
  clientsList?: unknown[] | null;
  /** The options actually built from that list. */
  options: Option[];
}): boolean {
  const { canActAsClient, selected, clientsList, options } = params;

  if (!canActAsClient) return false;
  // No selection, nothing to give up. Covers both '' and undefined.
  if (!selected) return false;

  // In flight. Retiring here would spend a valid selection on a slow network.
  if (!clientsList) return false;

  // A settled but empty list is not evidence that THIS client is gone — it is
  // equally the shape of a failed or still-warming response. Narrowing on it
  // would clear the selection every time the list came back empty for any
  // reason, so it is treated as "no information" rather than "not yours".
  if (options.length === 0) return false;

  return !options.some((option) => String(option.value) === String(selected));
}
